import React, {useEffect, useState} from "react";
import {Link} from "react-router";
import {
  Button,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import LinkIcon from "@material-ui/icons/Link";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLess";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import OutputWithTests from "common_components/flowmaster/output_with_tests";
import {EnhancedLlmRun, LlmRunId} from "common/flowmaster/types/llm_run";
import {OrganisationId} from "common/types/organisation";
import {DataKey} from "common/flowmaster/types/workflow";
import {
  GroupedResultEntry,
  GroupedResults,
  TestId,
} from "common/flowmaster/types/test";
import ConfigurationRunInputs from "./configuration_run_inputs";
import {Definition, DefinitionConfig} from "./data_assets_create_view";
import {
  PreviewPanel,
  PreviewPanelBody,
  PreviewPanelHeader,
} from "routes/data_assets/util/preview_panel";
import {RunWithFeedback} from "routes/data_assets/util/run_with_feedback_button";

export interface ConfigurationRunOutputProps {
  llmRun: EnhancedLlmRun;
  config: DefinitionConfig;
  definition: Definition;
  organisationId: OrganisationId;
  dataKey: DataKey;
  title: string;
  groupedResults?: GroupedResults<GroupedResultEntry>;
  onAddTest: (dataKey: DataKey, key: string) => void;
  onUpdateTest: (
    testId: TestId,
    testType: string,
    value: Record<string, unknown>,
  ) => void;
  onDeleteTest: (testId: TestId) => void;
  getTestResults: (dataKey: DataKey, key: string) => void;
  fetchLlmRun?: (organisationId: OrganisationId, runId: LlmRunId) => void;
  rerunTests?: (runId: LlmRunId) => void;
  onClose: () => void;
  view: string | null;
  setView: (value: string | null) => void;
  ignoreClickawayRefs: Array<React.RefObject<HTMLElement>>;
  isRefreshing: boolean;
  isRerunningTests: boolean;
}

export const ConfigurationRunOutput = (props: ConfigurationRunOutputProps) => {
  const {
    llmRun,
    organisationId,
    fetchLlmRun,
    rerunTests,
    definition,
    config,
    view,
    setView,
    onClose,
    ignoreClickawayRefs,
    isRefreshing,
    isRerunningTests,
    ...restProps
  } = props;
  const llmRunDocument = (llmRun.data.data_store.context
    .document_id as unknown) as string;
  const llmRunProject = (llmRun.data.data_store.context
    .project_id as unknown) as string;

  const initialView =
    view === "inputs" || view === "outputs" ? view : "outputs";
  const [selectedTab, setSelectedTab] = useState<"outputs" | "inputs">(
    initialView,
  );

  const [showRerunTestFeedback, setShowRerunTestFeedback] = useState(false);

  const handleTabChange = (
    event: React.ChangeEvent,
    newValue: "outputs" | "inputs",
  ) => {
    setSelectedTab(newValue);
    setView(newValue);
  };

  useEffect(() => {
    setSelectedTab(initialView);
  }, [llmRun?.id]);

  const handleRefresh = () => {
    if (llmRun?.id && fetchLlmRun) {
      fetchLlmRun(organisationId, llmRun.id);
    }
  };

  const handleRerunTests = () => {
    if (llmRun?.id && rerunTests) {
      rerunTests(llmRun.id);
      setShowRerunTestFeedback(true);

      setTimeout(() => {
        setShowRerunTestFeedback(false);
      }, 1500);
    }
  };

  return (
    <PreviewPanel onClose={onClose} ignoreClickawayRefs={ignoreClickawayRefs}>
      {({setIsInteracting, isFullscreen, setIsFullscreen}) => (
        <>
          <PreviewPanelHeader>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
            >
              <Tab label="Outputs" value="outputs" />
              <Tab label="Inputs" value="inputs" />
            </Tabs>
            <div
              style={{
                display: "flex",
                gap: "0.5em",
                alignItems: "center",
              }}
            >
              {(isRefreshing || isRerunningTests) && (
                <CircularProgress size={20} />
              )}
              <Link
                to={`/organisation/${organisationId}/llm_run/${llmRun.id}/detail`}
                style={{
                  color: "inherit",
                  display: "flex",
                  alignItems: "end",
                  textDecoration: "none",
                }}
              >
                <IconButton size="small">
                  <LinkIcon />
                </IconButton>
              </Link>
              <Button size="small" onClick={handleRefresh} variant="outlined">
                Refresh
              </Button>
              <RunWithFeedback
                bottom
                showStartedFeedback={showRerunTestFeedback}
              >
                <Button
                  size="small"
                  onClick={handleRerunTests}
                  variant="outlined"
                >
                  Rerun Tests
                </Button>
              </RunWithFeedback>
              <div style={{display: "flex", gap: "0.2em"}}>
                <IconButton
                  size="small"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  aria-label="Close"
                >
                  {isFullscreen ? <UnfoldLessIcon /> : <ZoomOutMapIcon />}
                </IconButton>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          </PreviewPanelHeader>
          <PreviewPanelBody>
            {selectedTab === "outputs" && (
              <>
                <OutputWithTests
                  llmRun={llmRun}
                  organisationId={organisationId}
                  onTestEditorOpen={() => setIsInteracting(true)}
                  onTestEditorClose={() => setIsInteracting(false)}
                  {...restProps}
                />
              </>
            )}
            {selectedTab === "inputs" && (
              <ConfigurationRunInputs
                downstreamRuns={llmRun.downstream_runs || []}
                organisationId={organisationId}
                selectedDocumentId={llmRunDocument}
                selectedProjectId={llmRunProject}
                selectedConfig={config}
                selectedDefinition={definition}
              />
            )}
          </PreviewPanelBody>
        </>
      )}
    </PreviewPanel>
  );
};
